import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createColumnHelper } from '@tanstack/react-table'
import _ from 'lodash'
import { Download } from 'lucide-react'
import { observer } from 'mobx-react'
import PropTypes from 'prop-types'
import { useCallback, useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router'

import { ActionButtons } from '@/components/ActionButtons'
import { ConfirmButton } from '@/components/ConfirmButton'
import { DataTable } from '@/components/DataTable'
import { DataTableFilter } from '@/components/DataTable/DataTableFilter'
import { Button } from '@/components/elements/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/elements/tabs'
import { Layout } from '@/components/Layout'
import { StatusBadge } from '@/components/StatusBadge'
import {
  approveUserExternalCertification,
  getUserExternalCertifications,
  rejectUserExternalCertification,
} from '@/services/external_certifications.service'
import { UserStoreContext } from '@/stores/UserStore'
import { formatDate } from '@/utils/formatters'
import { toast } from '@/utils/helpers'
import { formatUserName } from '@/utils/userHelpers'

import SubmitCertificationModal from './SubmitCertificationModal'

const EXTERNAL_CERTIFICATION_STATUS_OPTIONS = [
  { label: 'Pending', id: 'Pending' },
  { label: 'Approved', id: 'Approved' },
  { label: 'Denied', id: 'Denied' },
]

export function ExternalCertificationsTable({
  getCertifications,
  extraQueryKeys = {},
  showUser = false,
  approvalMode = false,
  setEditingCertification,
  actions = [],
}) {
  const [sorting, setSorting] = useState([{ id: 'created_at', desc: true }])
  const [pagination, setPagination] = useState({ pageSize: 20, pageIndex: 0 })
  const [filterValues, setFilterValues] = useState({})

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
  }, [filterValues])

  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')
  const queryClient = useQueryClient()

  const { mutate: doApprove } = useMutation({
    mutationFn: (payload) => approveUserExternalCertification(payload.entityId, payload.id),
    onError: (err) => handleError(err.message),
    onSuccess: () => {
      handleSuccess('Credential approved successfully')
      queryClient.invalidateQueries({ queryKey: ['external-certifications'] })
      queryClient.invalidateQueries({ queryKey: ['external-certifications-pending'] })
    },
  })

  const { mutate: doReject } = useMutation({
    mutationFn: (payload) =>
      rejectUserExternalCertification(payload.entityId, payload.id, payload.rejectionReason),
    onError: (err) => handleError(err.message),
    onSuccess: () => {
      handleSuccess('Credential rejected')
      queryClient.invalidateQueries({ queryKey: ['external-certifications'] })
      queryClient.invalidateQueries({ queryKey: ['external-certifications-pending'] })
    },
  })

  const { data, isPending } = useQuery({
    queryKey: ['external-certifications', sorting, pagination, filterValues, extraQueryKeys],
    queryFn: () =>
      getCertifications({
        sorting,
        pagination,
        searchTerm: filterValues.search,
        filters: _.omit(filterValues, 'search'),
      }),
    placeholderData: keepPreviousData,
  })

  const filters = [
    { type: 'input', label: 'Search', id: 'search' },
    {
      type: 'select',
      label: 'Status',
      id: 'status',
      options: EXTERNAL_CERTIFICATION_STATUS_OPTIONS,
    },
  ]

  const statusRenderer = useCallback(
    ({ getValue, row }) => {
      const status = getValue()
      const { approvedBy, entity, id, rejectionReason } = row.original
      // Handle entity as either an object (expanded) or a string/number (ID)
      const certEntityId = entity?.id ?? entity

      if (approvalMode && status === 'Pending') {
        return (
          <div className="flex flex-row items-center gap-2">
            <ConfirmButton
              confirmTitle="Approve Credential"
              confirmContent="Are you sure you want to approve this credential?"
              variant="outline"
              size="sm"
              onClick={() => doApprove({ id, entityId: certEntityId })}
            >
              Approve
            </ConfirmButton>
            <ConfirmButton
              confirmTitle="Reject Credential"
              confirmContent="Are you sure you want to reject this credential?"
              variant="destructive"
              size="sm"
              onClick={() => doReject({ id, entityId: certEntityId })}
            >
              Reject
            </ConfirmButton>
          </div>
        )
      }

      return (
        <div className="flex flex-col gap-1">
          <StatusBadge status={status} type="externalCertification" />
          {approvedBy && (
            <span className="text-muted-foreground text-xs">by {formatUserName(approvedBy)}</span>
          )}
          {status === 'Denied' && rejectionReason && (
            <span className="text-muted-foreground text-xs">{rejectionReason}</span>
          )}
        </div>
      )
    },
    [approvalMode, doApprove, doReject],
  )

  const actionsRenderer = useCallback(
    ({ row }) => {
      if (row.original.status === 'Pending') {
        return (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditingCertification(row.original)}
          >
            Edit
          </Button>
        )
      }
      return null
    },
    [setEditingCertification],
  )

  const filesRenderer = useCallback(({ getValue }) => {
    const files = getValue()
    if (!files || files.length === 0)
      return <span className="text-muted-foreground">No files</span>
    return (
      <div className="flex flex-row gap-2">
        {files.map((file) => (
          <a
            key={file.id || file.filePath}
            href={file.signedFilePath}
            rel="noreferrer"
            target="_blank"
            className="text-primary hover:text-foreground"
            title={file.fileName}
          >
            <Download className="h-4 w-4" />
          </a>
        ))}
      </div>
    )
  }, [])

  const coursesRenderer = useCallback(({ getValue }) => {
    const courses = getValue()
    if (!courses || courses.length === 0) {
      return <span className="text-muted-foreground">-</span>
    }
    return (
      <span className="max-w-[200px] truncate" title={courses.map((c) => c.name).join(', ')}>
        {courses.map((c) => c.name).join(', ')}
      </span>
    )
  }, [])

  const columnHelper = createColumnHelper()
  const columns = [
    ...(showUser
      ? [
          columnHelper.accessor((row) => formatUserName(row.user), {
            id: 'user',
            header: 'User',
          }),
        ]
      : []),
    columnHelper.accessor((row) => row.category?.name, {
      id: 'category',
      header: 'Category',
    }),
    columnHelper.accessor('courses', {
      header: 'Equivalent Courses',
      cell: coursesRenderer,
    }),
    columnHelper.accessor('completionDate', {
      header: 'Completion Date',
      cell: (info) => formatDate(info.getValue()),
    }),
    columnHelper.accessor('expiryDate', {
      header: 'Expiry Date',
      cell: (info) => (info.getValue() ? formatDate(info.getValue()) : '-'),
    }),
    columnHelper.accessor('files', {
      header: 'Files',
      cell: filesRenderer,
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: statusRenderer,
    }),
    ...(!approvalMode
      ? [
          columnHelper.display({
            id: 'actions',
            header: '',
            cell: actionsRenderer,
          }),
        ]
      : []),
  ]

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-row flex-wrap content-end justify-between">
        <DataTableFilter
          setFilters={setFilterValues}
          filterValues={filterValues}
          filters={filters}
        />
        <ActionButtons actions={actions} className="flex-wrap content-end" />
      </div>

      <DataTable
        columns={columns}
        data={data?.results || []}
        loading={isPending}
        pagination={pagination}
        rowCount={data?.count || 0}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
      />
    </div>
  )
}

ExternalCertificationsTable.propTypes = {
  getCertifications: PropTypes.func.isRequired,
  extraQueryKeys: PropTypes.object,
  showUser: PropTypes.bool,
  approvalMode: PropTypes.bool,
  setEditingCertification: PropTypes.func,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      type: PropTypes.string,
      onClick: PropTypes.func,
    }),
  ),
}

const ExternalCertifications = observer(() => {
  const { entityId } = useParams()
  const { user, canManageEntity } = useContext(UserStoreContext)

  const [showModal, setShowModal] = useState(false)
  const [editingCertification, setEditingCertification] = useState(null)

  const setAndEditCertification = (certification) => {
    setEditingCertification(certification)
    setShowModal(true)
  }

  const closeAndResetModal = () => {
    setEditingCertification(null)
    setShowModal(false)
  }

  const myCertificationsLoader = (params) =>
    getUserExternalCertifications({ ...params, entityId, userId: user.id })
  const allCertificationsLoader = (params) =>
    getUserExternalCertifications({ ...params, entityId })

  return (
    <>
      <Layout breadcrumbs={['External Credentials']}>
        <Layout.Heading title="External Credentials" />
        {canManageEntity && (
          <Tabs defaultValue="all" className="mt-4">
            <TabsList className="mb-4">
              <TabsTrigger value="all">All Credentials</TabsTrigger>
              <TabsTrigger value="mine">My Credentials</TabsTrigger>
            </TabsList>

            <TabsContent value="all" className="m-0 flex-1">
              <ExternalCertificationsTable
                getCertifications={allCertificationsLoader}
                extraQueryKeys={{ entity: entityId }}
                showUser
                approvalMode
              />
            </TabsContent>
            <TabsContent value="mine" className="m-0 flex-1">
              <ExternalCertificationsTable
                getCertifications={myCertificationsLoader}
                extraQueryKeys={{ user: user.id, entity: entityId }}
                setEditingCertification={setAndEditCertification}
                actions={[
                  {
                    label: 'Submit Credential',
                    type: 'submit',
                    onClick: () => setShowModal(true),
                  },
                ]}
              />
            </TabsContent>
          </Tabs>
        )}
        {!canManageEntity && (
          <ExternalCertificationsTable
            getCertifications={myCertificationsLoader}
            extraQueryKeys={{ user: user.id, entity: entityId }}
            setEditingCertification={setAndEditCertification}
            actions={[
              { label: 'Submit Credential', type: 'submit', onClick: () => setShowModal(true) },
            ]}
          />
        )}
      </Layout>
      <SubmitCertificationModal
        setOpen={closeAndResetModal}
        open={showModal}
        editingCertification={editingCertification}
        key={editingCertification?.id}
      />
    </>
  )
})

export default ExternalCertifications
